import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Skeleton,
  Stack,
  TextField,
} from '@mui/material'
import AdminMainContainer from '../../../../layouts/admin/AdminMainContainer'
import useAdminLeaveRequestEditViewModel from './viewModel'
import IconSave from '@mui/icons-material/Save'
import { Controller } from 'react-hook-form'
import { AccountTeam } from '../../../../database/enums/AccountTeam.enum'
import AccountTeamEnumHelper from '../../../../database/enums/helpers/account-team.helper'
import { LeaveTime } from '../../../../database/enums/LeaveTime.enum'
import LeaveTimeEnumHelper from '../../../../database/enums/helpers/leave-time.helper'
import { DatePicker } from '@mui/x-date-pickers'
import { LeaveType } from '../../../../database/enums/LeaveType.enum'
import LeaveTypeEnumHelper from '../../../../database/enums/helpers/leave-type.helper '
import { LeaveRequestSource } from '../../../../database/enums/LeaveRequestSource.enum'
import LeaveRequestSourceEnumHelper from '../../../../database/enums/helpers/leave-request-source.helper'
import { ApprovalStatus } from '../../../../database/enums/ApprovalStatus.enum'

const scopeNamePrefix = 'admin-leave-request-edit'

export default function AdminLeaveRequestEditPage() {
  const viewModel = useAdminLeaveRequestEditViewModel()

  const renderLoadingState = () => {
    return (
      <Box sx={{ width: '100%', maxWidth: '500px' }}>
        <Card elevation={5}>
          <CardContent>
            <Stack gap={2}>
              <Skeleton variant='text' />
              <Skeleton variant='rounded' height={48} />
              <Skeleton variant='rectangular' height={96} />
            </Stack>
          </CardContent>
        </Card>
      </Box>
    )
  }

  const renderDetailState = () => {
    if (!!viewModel.detailData)
      return (
        <Box sx={{ width: '100%', maxWidth: '500px' }}>
          <form onSubmit={viewModel.formHook.handleSubmit(viewModel.onSubmit)}>
            <Card elevation={5}>
              <CardContent>
                <Stack gap={2}>
                  <Controller
                    control={viewModel.formHook.control}
                    name='source'
                    render={({ field }) => (
                      <FormControl fullWidth required sx={{ px: 1 }}>
                        <FormLabel id={`${scopeNamePrefix}-source-label`}>Source</FormLabel>
                        <RadioGroup row {...field}>
                          {Object.values(LeaveRequestSource).map((item, index) => (
                            <FormControlLabel
                              key={`${scopeNamePrefix}-source-option-${index}`}
                              value={item}
                              control={<Radio />}
                              label={LeaveRequestSourceEnumHelper.getLabel(item)}
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>
                    )}
                  />
                </Stack>
              </CardContent>
              <Divider />
              <CardContent>
                <Stack gap={2}>
                  <Controller
                    control={viewModel.formHook.control}
                    name='name'
                    render={({ field, fieldState }) => (
                      <TextField
                        variant='outlined'
                        required
                        label='Name'
                        placeholder='Enter your name...'
                        {...field}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                  <Controller
                    control={viewModel.formHook.control}
                    name='email'
                    render={({ field, fieldState }) => (
                      <TextField
                        variant='outlined'
                        required
                        label='Email'
                        placeholder='Enter your email...'
                        type='email'
                        {...field}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                  <Controller
                    control={viewModel.formHook.control}
                    name='accountTeam'
                    render={({ field }) => (
                      <FormControl fullWidth required sx={{ px: 1 }}>
                        <FormLabel id={`${scopeNamePrefix}-team-label`}>Team</FormLabel>
                        <RadioGroup row {...field}>
                          {Object.values(AccountTeam).map((item, index) => (
                            <FormControlLabel
                              key={`${scopeNamePrefix}-team-option-${index}`}
                              value={item}
                              control={<Radio />}
                              label={AccountTeamEnumHelper.getLabel(item)}
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>
                    )}
                  />
                  <Controller
                    control={viewModel.formHook.control}
                    name='position'
                    render={({ field, fieldState }) => (
                      <TextField
                        variant='outlined'
                        required
                        label='Position'
                        placeholder='Enter your position...'
                        {...field}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </Stack>
              </CardContent>
              <Divider />
              <CardContent>
                <Stack gap={2}>
                  <Controller
                    control={viewModel.formHook.control}
                    name='leaveTime'
                    render={({ field }) => (
                      <FormControl fullWidth required sx={{ px: 1 }}>
                        <FormLabel id={`${scopeNamePrefix}-leaveTime-label`}>Leave Time</FormLabel>
                        <RadioGroup row {...field}>
                          {Object.values(LeaveTime).map((item, index) => (
                            <FormControlLabel
                              key={`${scopeNamePrefix}-leaveTime-option-${index}`}
                              value={item}
                              control={<Radio />}
                              label={LeaveTimeEnumHelper.getLabel(item)}
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>
                    )}
                  />
                  <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                    <Controller
                      control={viewModel.formHook.control}
                      name='startDate'
                      render={({ field, fieldState }) => (
                        <DatePicker
                          label='Start Date'
                          {...field}
                          onChange={newVal => {
                            const endDate = viewModel.formHook.getValues('endDate')
                            field.onChange(newVal)
                            if (!!newVal && !!endDate && newVal > endDate)
                              viewModel.formHook.setValue('endDate', newVal, { shouldValidate: true })
                          }}
                          slotProps={{
                            textField: {
                              required: true,
                              error: !!fieldState.error,
                              helperText: fieldState.error?.message,
                            },
                          }}
                          sx={{ flex: 1 }}
                        />
                      )}
                    />
                    <Controller
                      control={viewModel.formHook.control}
                      name='endDate'
                      render={({ field, fieldState }) => (
                        <DatePicker
                          label='End Date'
                          minDate={viewModel.formHook.watch('startDate')}
                          {...field}
                          slotProps={{
                            textField: {
                              required: true,
                              error: !!fieldState.error,
                              helperText: fieldState.error?.message,
                            },
                          }}
                          sx={{ flex: 1 }}
                        />
                      )}
                    />
                  </Stack>
                  <Controller
                    control={viewModel.formHook.control}
                    name='leaveType'
                    render={({ field }) => (
                      <FormControl fullWidth required>
                        <InputLabel id={`${scopeNamePrefix}-leaveType-label`}>Type of leave</InputLabel>
                        <Select
                          labelId={`${scopeNamePrefix}-leaveType-label`}
                          label='Type of leave'
                          {...field}
                          onChange={newValue => {
                            field.onChange(newValue)
                            viewModel.formHook.trigger('otherLeaveType')
                          }}
                        >
                          {Object.values(LeaveType).map((item, index) => (
                            <MenuItem key={`${scopeNamePrefix}-leaveType-option-${index}`} value={item}>
                              {LeaveTypeEnumHelper.getLabel(item)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                  {viewModel.formHook.watch('leaveType') === LeaveType.OTHER ? (
                    <Controller
                      control={viewModel.formHook.control}
                      name='otherLeaveType'
                      render={({ field }) => (
                        <TextField
                          variant='outlined'
                          required
                          label='Other Leave Type'
                          placeholder='Enter your leave type...'
                          {...field}
                        />
                      )}
                    />
                  ) : null}
                </Stack>
              </CardContent>
              <Divider />
              <CardContent>
                <Stack gap={2}>
                  <Controller
                    control={viewModel.formHook.control}
                    name='reason'
                    render={({ field }) => (
                      <TextField
                        variant='outlined'
                        label='Reason for leave'
                        placeholder='Enter your reasons...'
                        multiline
                        minRows={3}
                        {...field}
                      />
                    )}
                  />
                </Stack>
              </CardContent>
              <Divider />
              <CardActions sx={{ justifyContent: 'end' }}>
                <Button onClick={viewModel.resetPage} variant='text'>
                  Reset form
                </Button>
                <Button
                  type='submit'
                  variant='contained'
                  startIcon={<IconSave />}
                  disabled={viewModel.dataUpdateStatus.isLoading}
                  loading={viewModel.dataUpdateStatus.isLoading}
                >
                  Save
                </Button>
              </CardActions>
            </Card>
          </form>
        </Box>
      )
  }

  const renderErrorState = () => {
    return (
      <Alert severity='error' variant='filled'>
        <AlertTitle>Error!</AlertTitle>
        Document not found!
      </Alert>
    )
  }

  console.log(viewModel.dataFetchStatus.isError || viewModel.detailData?.approvalStatus !== ApprovalStatus.UNRESPONDED)

  return (
    <AdminMainContainer title='Leave Request Edit' backButton={{ show: true }}>
      {viewModel.dataFetchStatus.isLoading
        ? renderLoadingState()
        : viewModel.dataFetchStatus.isSuccess && !!viewModel.detailData
          ? renderDetailState()
          : viewModel.dataFetchStatus.isError
            ? renderErrorState()
            : null}
    </AdminMainContainer>
  )
}
